import type { ReactNode } from 'react'
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Hourglass, Info } from 'lucide-react'
import type { AdPeriodWindowDto } from '@lib/dto/ad-numbers.dto'
import type { LeadStatsTrendPointDto } from '@lib/dto/lead-stats-numbers.dto'
import { LEAD_STATS_VIEWS, type LeadStatsView } from '@lib/enums/lead-stats-view.enum'
import type { CalendarDay } from '@lib/validators/temporal.validator'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { SegmentedControl, type SegmentedOption } from '@/components/ui/segmented-control'
import { Tooltip } from '@/components/ui/tooltip'
import { formatAdDay, formatAdPeriod, formatCount, formatMicros } from '@/lib/adFormat'
import { cn } from '@/lib/utils'

/**
 * What the Leads, Pipeline and Ads pages share, so they read alike
 * (docs/pipeline-view.spec.md): a number's tile, a change from the period
 * before, a twelve-period trend, the still-moving mark, the period bar and the
 * view toggle.
 */

/** A share as a whole percentage: `19%`. */
export function wholePercent(fraction: number): string {
  return new Intl.NumberFormat(undefined, { style: 'percent', maximumFractionDigits: 0 }).format(fraction)
}

export interface ChangeText {
  text: string
  up: boolean | null
}

/** A change from the period before: how much, which way, and whether to color it. */
export function changeOf(value: number | null, previous: number | null): ChangeText | null {
  if (value === null || previous === null) return null
  const diff = value - previous
  if (diff === 0) return { text: 'No change', up: null }
  const pct = previous >= 10 ? ` (${wholePercent(Math.abs(diff) / previous)})` : ''
  return { text: `${formatCount(Math.abs(diff))}${pct}`, up: diff > 0 }
}

export function Change({ change, against, sameAge }: { change: ChangeText | null; against: string | null; sameAge: boolean }) {
  if (change === null) return <p className="m-0 h-[17px] text-small text-ink-muted">No comparison</p>
  const Icon = change.up === true ? ArrowUp : ArrowDown
  return (
    <p className="m-0 flex flex-wrap items-center gap-1 text-small">
      <span
        className={cn(
          'inline-flex items-center gap-0.5 font-medium',
          change.up === true && 'text-ok-ink',
          change.up === false && 'text-danger-ink',
          change.up === null && 'text-ink-muted',
        )}
      >
        {change.up === null ? null : <Icon className="h-3 w-3" aria-label={change.up ? 'Up' : 'Down'} />}
        {change.text}
      </span>
      {against === null ? null : (
        <span className="text-ink-muted">
          vs {against}
          {sameAge ? ' at the same age' : ''}
        </span>
      )}
    </p>
  )
}

/** Twelve periods as small bars: this one in the accent, ones still moving lighter. */
export function Trend({ points, format, label }: { points: LeadStatsTrendPointDto[]; format: (v: number) => string; label: string }) {
  const max = Math.max(0, ...points.map((p) => p.value ?? 0))
  return (
    <div role="img" aria-label={`${label}, last ${String(points.length)} periods`} className="mt-1 flex h-7 items-end gap-0.5">
      {points.map((point, i) => {
        const current = i === points.length - 1
        const height = point.value === null || max === 0 ? 1 : Math.max(2, Math.round((point.value / max) * 28))
        return (
          <Tooltip key={point.start} content={`${formatAdDay(point.start)}: ${point.value === null ? 'not counted' : format(point.value)}`}>
            <div
              className={cn(
                'flex-1 rounded-t-sm',
                current && 'bg-primary',
                !current && point.stillMoving && 'bg-line',
                !current && !point.stillMoving && 'bg-line-strong/50',
              )}
              style={{ height }}
            />
          </Tooltip>
        )
      })}
    </div>
  )
}

export function StillMoving() {
  return (
    <span className="inline-flex items-center gap-1 rounded-sm bg-past-soft px-1.5 text-xs font-medium leading-[18px] text-past-ink">
      <Hourglass className="h-[11px] w-[11px]" aria-hidden="true" />
      Still moving
    </span>
  )
}

/** A quiet note under the numbers, with an info mark. */
export function InfoNote({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p className={cn('m-0 flex items-start gap-2 text-small text-ink-muted', className)}>
      <Info className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      {children}
    </p>
  )
}

interface PeriodBarProps {
  /** The days shown: one period, or a span of them ending with its newest. */
  window: AdPeriodWindowDto
  /** Newest first. */
  periods: { start: CalendarDay; inProgress: boolean }[]
  spend: { spendMicros: number; currencyCode: string } | null
  /** Called with the newest period of the days to show next. */
  onPeriod: (start: CalendarDay) => void
  /** How many periods the arrows move: a span's length, so the next span doesn't overlap this one. One by default. */
  step?: number
}

/** The days shown, the way to the ones either side, what they're compared with, and their ad spend. */
export function PeriodBar({ window, periods, spend, onPeriod, step = 1 }: PeriodBarProps) {
  // The newest period shown is the one holding the window's last day.
  const index = periods.findIndex((p) => p.start <= window.end)
  // From a span stepped forward into the period still in progress, back is the
  // newest finished one, so the arrows return to the spans they came from.
  const finished = periods.findIndex((p) => !p.inProgress)
  const older = index >= 0 && index < finished ? periods[finished] : periods[index + step]
  // Forward a whole step where there is one, else as far as the newest period goes.
  const newer = index > 0 ? (periods[index - step] ?? periods[0]) : undefined
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-surface-sunken px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          aria-label={step === 1 ? 'Previous period' : 'Earlier weeks'}
          disabled={older === undefined}
          onClick={() => older && onPeriod(older.start)}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="min-w-[11rem] text-center text-sm font-medium text-ink">
          {formatAdPeriod(window.start, window.end)}
          {window.inProgress ? <span className="block text-xs font-normal text-ink-muted">In progress · through {formatAdDay(window.through)}</span> : null}
        </span>
        <Button
          variant="outline"
          size="sm"
          aria-label={step === 1 ? 'Next period' : 'Later weeks'}
          disabled={newer === undefined}
          onClick={() => newer && onPeriod(newer.start)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <span className="text-small text-ink-muted">Compared with {formatAdPeriod(window.compareStart, window.compareEnd)}</span>
      </div>
      {spend === null ? null : (
        <div className="flex items-baseline gap-2">
          <span className="text-xs font-medium text-ink-muted">Ad spend</span>
          <span className="font-mono text-sm font-medium text-ink">{formatMicros(spend.spendMicros, spend.currencyCode)}</span>
        </div>
      )}
    </div>
  )
}

const VIEW_LABELS: Readonly<Record<LeadStatsView, string>> = { cohort: 'Leads from this period', activity: 'What happened this period' }

/**
 * The two ways to count a period. A page that follows couples rather than leads
 * names them its own way: the Pipeline's "Couples who inquired" / "Everything
 * that happened".
 */
export function ViewToggle({
  view,
  onView,
  labels = VIEW_LABELS,
}: {
  view: LeadStatsView
  onView: (view: LeadStatsView) => void
  labels?: Readonly<Record<LeadStatsView, string>>
}) {
  const options: SegmentedOption<LeadStatsView>[] = LEAD_STATS_VIEWS.map((value) => ({ value, label: labels[value] }))
  return <SegmentedControl label="View" size="sm" value={view} options={options} onValueChange={onView} />
}

/**
 * A count's last twelve months as a line, period by period, for a headline
 * number's tile: the shape of the year, not its values, which the trend's
 * tooltip-bearing bars give elsewhere. A period not counted (before records
 * began) breaks the line rather than drawing a fall to zero.
 */
export function Sparkline({ points, label }: { points: readonly LeadStatsTrendPointDto[]; label: string }) {
  const counted = points.flatMap((p) => (p.value === null ? [] : [p.value]))
  if (counted.length < 2 || points.length < 2) return null
  const max = Math.max(...counted)
  const min = Math.min(...counted)
  const step = 120 / (points.length - 1)
  const y = (v: number): number => (max === min ? 14 : 25 - ((v - min) / (max - min)) * 22)
  // Runs of counted periods, each its own line.
  const runs: string[][] = [[]]
  points.forEach((p, i) => {
    if (p.value === null) runs.push([])
    else runs[runs.length - 1]?.push(`${(i * step).toFixed(1)},${y(p.value).toFixed(1)}`)
  })
  return (
    <svg viewBox="0 0 120 28" preserveAspectRatio="none" className="mt-1 h-7 w-full" role="img" aria-label={`${label}, last 12 months`}>
      {runs
        .filter((run) => run.length > 1)
        .map((run) => (
          <polyline
            key={run[0]}
            points={run.join(' ')}
            className="fill-none stroke-primary"
            strokeWidth={1.75}
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        ))}
    </svg>
  )
}

interface StatTileProps {
  /** What is counted. */
  label: string
  /** The period's count can still rise; the chip's row is kept on every tile either way, so the numbers line up. */
  stillMoving: boolean
  /** The number (`StatValue`), then what explains it: the change, the trend, the cost. */
  children: ReactNode
}

/** One headline number in a row of them: its name, the number, and what follows it. */
export function StatTile({ label, stillMoving, children }: StatTileProps) {
  return (
    <Card className="flex min-w-0 flex-[1_1_140px] flex-col gap-1.5 p-4">
      <div className="flex flex-col items-start gap-1">
        <span className="text-xs font-medium text-ink-muted">{label}</span>
        {stillMoving ? <StillMoving /> : <span className="h-[18px]" aria-hidden="true" />}
      </div>
      {children}
    </Card>
  )
}

/** A tile's number, in the stat size; `missing` draws the dash for one not counted. */
export function StatValue({ children, missing = false }: { children: ReactNode; missing?: boolean }) {
  return <p className={cn('m-0 text-stat font-semibold', missing ? 'text-ink-subtle' : 'text-ink')}>{children}</p>
}

/** The cost of each one counted, at the foot of a tile. */
export function StatCost({ children }: { children: ReactNode }) {
  return <p className="m-0 mt-0.5 border-t border-line pt-2 font-mono text-xs text-ink-muted">{children}</p>
}
